import { BoxProps } from '@mui/material';
import * as CSS from 'csstype';
import { useFocusScroll } from '../../../../../hooks/useFocusScroll';
import { NavigableElement } from '../../../CfDataGridProps';
import { ValueGetter } from '../CfDataGridTableViewProps';
import { CfDataGridSkeletonRow } from './CfDataGridSkeletonRow';
import { CfDataGridRowProps } from './common';
import { StyledGridCell, StyledGridRow } from './row.styles';

export function CfDataGridRow<TItem>(props: CfDataGridRowProps<TItem>, rowPropsOverride?: BoxProps): JSX.Element {
  const { item, itemIndex } = props;
  if (!item) {
    return <CfDataGridSkeletonRow tableViewDefinition={props.tableViewDefinition} itemIndex={itemIndex} />;
  }

  const virtualizedRow = props.tableViewDefinition.virtualizedProps?.enabled ?? false;
  const [elementRef, handleFocus] = useFocusScroll<HTMLTableRowElement>(0, virtualizedRow);

  let cursorStyle: CSS.Property.Cursor = 'default';
  if (props.onItemClicked) {
    cursorStyle = 'pointer';
  }

  if (props.cursorStyle !== undefined) {
    if (typeof props.cursorStyle === 'function') {
      cursorStyle = props.cursorStyle(item, 'table');
    } else {
      cursorStyle = props.cursorStyle;
    }
  }

  let hover = true;

  if (props.hoverStyle !== undefined) {
    if (typeof props.hoverStyle === 'function') {
      hover = props.hoverStyle(item, 'table');
    } else {
      hover = props.hoverStyle;
    }
  }

  const bodyTableRowProps =
    typeof props.tableViewDefinition.bodyTableRowProps === 'function'
      ? props.tableViewDefinition.bodyTableRowProps(item, itemIndex)
      : props.tableViewDefinition.bodyTableRowProps;

  const augmentedRowSx = { ...bodyTableRowProps?.sx, ...{ cursor: cursorStyle } };

  return (
    <StyledGridRow
      className='CfTableRow-row'
      ref={elementRef}
      onClick={(event) => {
        // eslint-disable-next-line
        const target = event.target as unknown as any;
        if (target?.nodeName === 'BUTTON' || target?.offsetParent?.nodeName === 'BUTTON') return;
        props.onItemClicked?.(item, 'table', props.itemIndex);
      }}
      onKeyDown={(event: React.KeyboardEvent<HTMLTableRowElement>) => {
        // eslint-disable-next-line
        const target = event.target as unknown as any;
        if (target?.nodeName === 'BUTTON' || target?.offsetParent?.nodeName === 'BUTTON') return;
        if (event.key === 'Enter') props.onItemClicked?.(item, 'table', props.itemIndex);
      }}
      //hover={hover}
      {...bodyTableRowProps}
      {...rowPropsOverride}
      sx={augmentedRowSx}
      onFocus={handleFocus}
    >
      {props.valueGetters.map((getter: ValueGetter<TItem>, index: number) => {
        const bodyCellProps =
          typeof getter.column.bodyCellProps === 'function'
            ? getter.column.bodyCellProps(props.item, props.itemIndex)
            : getter.column.bodyCellProps;
        return (
          <StyledGridCell
            key={index}
            {...bodyCellProps}
            sx={{ textAlign: getter.column.horizontalAlignment, width: getter.column.width, ...bodyCellProps?.sx }}
          >
            {getter.getValue({
              item: item,
              itemIndex: props.itemIndex,
              filterText: props.filterText,
              setNavigableElement: (navigableElement: NavigableElement, columnIndex?: number) =>
                props.setNavigableElement(navigableElement, props.itemIndex, columnIndex ?? index),
              recomputeRowHeights: props.recomputeRowHeights,
              recomputeRowHeight: props.recomputeRowHeight,
            })}
          </StyledGridCell>
        );
      })}
    </StyledGridRow>
  );
}
